import Rendable from "@/glxp/abstract/rendable"
import Shader from "@/glxp/utils/shader"
import Node from "@/glxp/abstract/node"
import RAF from '@/js/utils/raf'

import GEOM from "@/glxp/geom/plane"
import ShaderManifest from "../shaderManifest"
import { vec2, vec3, mat4 } from 'gl-matrix'

let _NV = mat4.create()


class Mesh extends Rendable {
  constructor(scene, geom, shader, texture, section = "global") {
    super(scene)

    this.gl = scene.gl
    this.scene = scene
    this.geom = geom
    this.name = geom.name
    this.section = section

    this.node = new Node()
    this.shader = new Shader(ShaderManifest[shader])
    this.texture = texture
    this.active = true
    this.depthTest = true
    this.depthWrite = true
    this.useNoiseTexture = true
    this.useScreenSpaceNoise = true
    this.side = "both"
    this.mask = 0

    this.node.position  = vec3.clone(geom.translate)
    this.node.scale     = vec3.clone(geom.scale)
    this.node.rotation  = vec3.clone(geom.rotation)
    this.opacity = 1
    this.time = 0

    this.proxies = []
    this.useProxies = false

    this.hasNormal = true
    this.initBuffer({
      vertices: geom.vertices,
      uvs: geom.uvs,
      indices: geom.indices,
      normal: geom.normal,
    })

    this.initProgram(this.shader.vert, this.shader.frag)
    this.initVao()
    this.initMatrix()
    this.createUniforms()
  }

  createUniforms() {
    this.createUniform('uNMatrix', 'mat4')
    this.createUniform("uTexture", "texture")
    this.createUniform("uGrain", "texture")
    this.createUniform("uOpacity")
    this.createUniform("uGrainValue")
    this.createUniform("uGrainScale")
    this.createUniform("uTime")
    this.createUniform("uUseNoiseTexture")
    this.createUniform("uUseScreenSpaceNoise")
  }

  applyState() {
    let gl = this.gl
    this.scene.applyDefaultState()
    if (this.depthTest == false) {
      gl.disable(gl.DEPTH_TEST)
    }
    gl.depthMask(this.depthWrite)
    if (this.side == "front") {
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.FRONT);
    } else if (this.side == "back"){
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.BACK);
    }

  }

  preRender() {
    this.time += RAF.dt/1000
  }

  render(mask = 0) {
    if (!this.active || this.mask !== mask || this.scene.activeScenes.indexOf(this.section) < 0) {
      
      return
    }
    this.preRender()

    let gl = this.gl
    gl.useProgram(this.program)
    gl.bindVertexArray(this.vao)
    this.applyState()

    mat4.mul(_NV, this.scene.camera.getViewMatrix(), this.node.getMatrix())
    mat4.invert(_NV, _NV)
    mat4.transpose(_NV, _NV)
    this.bindUniform('uNMatrix', _NV)

    if (this.texture) {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(
        gl.TEXTURE_2D,
        this.scene.textureLoader.getTexture(this.texture)
      )
      this.bindUniform("uTexture", 0)
    }

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("Grain")
    )
    this.bindUniform('uGrain', 1)
    

    this.bindUniform('uOpacity', this.opacity)
    this.bindUniform('uTime', this.time)
    this.bindUniform('uGrainValue', this.scene.config.grain.value)
    this.bindUniform('uGrainScale', this.useScreenSpaceNoise ? this.scene.config.grainScale.value : this.scene.config.grainScaleMesh.value)
    this.bindUniform('uUseNoiseTexture', this.useNoiseTexture ? 1 : 0)
    this.bindUniform('uUseScreenSpaceNoise', this.useScreenSpaceNoise ? 1 : 0)
    

    if (this.useProxies) {
      this.bindUniform('uPMatrix', this.scene.camera.getProjectionMatrix())
      this.bindUniform('uVMatrix', this.scene.camera.getViewMatrix())

      for (let i = 0; i < this.proxies.length; i++) {
        this.bindUniform('uMMatrix', this.proxies[i].getMatrix())
        gl.drawElements(
          gl.TRIANGLES,
          this.geom.indices.length,
          gl.UNSIGNED_SHORT,
          0
        )
      }

    } else {
      this.bindMatrixUniforms(this.scene.camera)
      gl.drawElements(
        gl.TRIANGLES,
        this.geom.indices.length,
        gl.UNSIGNED_SHORT,
        0
      )
    }
    
    gl.bindVertexArray(null)
  }
}

export default Mesh
